import { isEqual, omit } from 'lodash';
import { useCallback, useMemo, useState } from 'react';
import { v4 as uuid } from '@upright/common/utils/uuid';

export interface EditingModeState<T extends { key: string; _uiId?: string }> {
  isEditing: boolean;
  editedData: T[];
  initialData: T[];
  hasChanges: boolean;
  startEditing: () => void;
  cancelEditing: () => void;
  exitEditing: () => void;
  updateData: (newData: T[]) => void;
  updateRow: (rowUiId: string | undefined, updates: Partial<T>) => void;
  deleteRow: (rowUiId: string | undefined) => void;
  addRow: () => void;
}

/**
 * Hook for managing edit mode state for operational data forms
 * Provides consistent edit/view mode behavior across all forms
 */
export function useEditingMode<T extends { key: string; _uiId?: string }>(
  initialData: T[],
  emptyRow?: T,
): EditingModeState<T> {
  const [isEditing, setIsEditing] = useState(false);
  const [editedData, setEditedData] = useState(initialData);

  // Compute hasChanges by comparing data instead of tracking in state
  // Strip _uiId before comparing since it's only for UI state management
  const hasChanges = useMemo(() => {
    const stripUiId = (data: T[]) => data.map(row => omit(row, '_uiId'));
    return !isEqual(stripUiId(initialData), stripUiId(editedData));
  }, [initialData, editedData]);

  const startEditing = useCallback(() => {
    setIsEditing(true);
    // Assign _uiId to all rows for consistent identification during editing
    const dataWithIds = initialData.map(row => ({
      ...row,
      _uiId: row._uiId ?? uuid(),
    }));
    setEditedData(dataWithIds);
  }, [initialData]);

  const cancelEditing = useCallback(() => {
    setIsEditing(false);
    setEditedData(initialData);
  }, [initialData]);

  const exitEditing = useCallback(() => {
    setIsEditing(false);
  }, []);

  const updateData = useCallback((newData: T[]) => {
    setEditedData(newData);
  }, []);

  // Array manipulation helpers for table data
  // Uses _uiId for row identification to handle rows with duplicate/empty keys
  const updateRow = useCallback((rowUiId: string | undefined, updates: Partial<T>) => {
    if (!rowUiId) return;
    setEditedData(prev => {
      return prev.map(row => (row._uiId === rowUiId ? { ...row, ...updates } : row));
    });
  }, []);

  const deleteRow = useCallback((rowUiId: string | undefined) => {
    if (!rowUiId) return;
    setEditedData(prev => prev.filter(row => row._uiId !== rowUiId));
  }, []);

  // TODO: Get rid of the magic hidden logic of addRow not doing anything if emptyRow is not given
  const addRow = useCallback(() => {
    if (emptyRow) {
      setEditedData(prev => {
        const newRow = { ...emptyRow, _uiId: uuid() };
        return [newRow, ...prev];
      });
    }
  }, [emptyRow]);

  return {
    isEditing,
    editedData,
    initialData,
    hasChanges,
    startEditing,
    cancelEditing,
    exitEditing,
    updateData,
    updateRow,
    deleteRow,
    addRow,
  };
}
